<script setup lang="ts">
import {
  PLAN_PROVIDER_LABELS,
  formatWeekdayShort,
  planAlerts,
  planGuide,
  planLaterSteps,
  planLinks,
  planProjectType,
  planSlot,
  planSubcontracting,
  planSubSteps,
  planSubmitLabel,
  planTimeline,
  type PlanLaterStep,
  type PlanStep,
  type QuoteSubcontracting,
} from '@cockpit/shared';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { RESULT_CLASSES, RESULT_LABELS } from '../plans';
import { useAuthStore } from '../stores/auth';
import { usePlanStore } from '../stores/plan';
import ConsequencesBlock from './ConsequencesBlock.vue';
import SubcontractingBlock from './subcontracting/SubcontractingBlock.vue';
import TimeSelect from './TimeSelect.vue';

/** Écran de validation (maquette `#plan`) : monté une fois dans la coquille, piloté par `usePlan()`. */
const store = usePlanStore();
const auth = useAuthStore();
const goButton = ref<HTMLButtonElement | null>(null);
/** Liens proposés par le plan (`context.links`), ex. « Ouvrir Axonaut » pour une facture à créer à la main. */
const links = computed(() => planLinks(store.plan?.context));
/** Mode d'emploi propre au plan (ex. facture à créer dans Axonaut) et libellé du bouton. */
const guide = computed(() => planGuide(store.plan?.context));
/** Lot AF : étapes qui complètent l'étape précédente (« Inviter le client »), affichées en retrait. */
const SUB_STEP_OPS = new Set(['calendar.inviteAttendees']);
const submitLabel = computed(() => planSubmitLabel(store.plan?.context));
/** Lot BE : étapes en retrait propres au plan (`context.subSteps`, ex. « Noter … comme prévenu »). */
const subSteps = computed(() => new Set(planSubSteps(store.plan?.context)));
/**
 * Lot BE : étapes grisées « plus tard » (`context.laterSteps`, ex. « Mettre à jour le site de … », lot BH) : affichées
 * avant l'étape `beforeKey`, jamais exécutables (ce ne sont pas des étapes du plan).
 */
const laterSteps = computed(() => planLaterSteps(store.plan?.context));
const stepKeys = computed(() => new Set(store.plan?.steps.map((s) => s.key) ?? []));
const laterBefore = (key: string): PlanLaterStep[] => laterSteps.value.filter((l) => l.beforeKey === key);
const laterAtEnd = computed(() =>
  laterSteps.value.filter((l) => l.beforeKey === null || !stepKeys.value.has(l.beforeKey)),
);
/** Lot U : avertissements du plan (projet déjà en cours, devis différents…). */
const alerts = computed(() => planAlerts(store.plan?.context));
/** Date et heure modifiables (lot K, « Fixer le R3 ») : les changer reconstruit le plan. */
const slot = computed(() => planSlot(store.plan?.context));
/** Lot AL : type de projet modifiable (« Préparer le projet ») et résumé des dates. */
const projectType = computed(() => planProjectType(store.plan?.context));
const timeline = computed(() => planTimeline(store.plan?.context));
/** Lot AY : bloc « Sous-traitance » de « Préparer le projet » (saisie interne, enregistrée tout de suite). */
const subcontracting = computed(() => planSubcontracting(store.plan?.context));

/** Lot AY : une ligne de maquette sous-traitée coche « Maquette sous-traitée » (le plan est reconstruit). */
function onSubcontractingSaved(value: QuoteSubcontracting): void {
  if (value.mockupSubcontracted && subcontracting.value && !subcontracting.value.subcontracted)
    void store.rebuild({ subcontracted: true });
}

/**
 * Lot AT : liens rendus par les étapes faites (`output.link`, ex. « Ouvrir le devis D-… dans Axonaut » après la
 * création du devis), affichés avec les résultats.
 */
const resultLinks = computed(() =>
  Object.values(store.shown).flatMap((result) => {
    const link = (result.output as { link?: { label?: unknown; url?: unknown } } | undefined)?.link;
    return result.status === 'ok' &&
      link &&
      typeof link.label === 'string' &&
      typeof link.url === 'string' &&
      /^https?:\/\//.test(link.url)
      ? [{ label: link.label, url: link.url }]
      : [];
  }),
);

function onProjectType(value: 'evolusite' | 'partner'): void {
  if (!projectType.value || value === projectType.value.value) return;
  void store.rebuild({ projectType: value });
}

function onSlotChange(field: 'date' | 'time', value: string): void {
  const current = slot.value;
  if (!current || !value || value === current[field]) return;
  void store.reschedule(field === 'date' ? value : current.date, field === 'time' ? value : current.time);
}

function draftOf(step: PlanStep) {
  return store.drafts.find((d) => d.key === step.key);
}

function chip(step: PlanStep): { text: string; cls: string } {
  const result = store.shown[step.key];
  if (result) return { text: RESULT_LABELS[result.status], cls: RESULT_CLASSES[result.status] };
  return { text: PLAN_PROVIDER_LABELS[step.via], cls: 'c-mut' };
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape' && store.isOpen) store.cancel();
}

watch(
  () => store.phase,
  async (phase, previous) => {
    if (phase === 'review' && previous === 'closed') {
      await nextTick();
      goButton.value?.focus();
    }
  },
);

onMounted(() => document.addEventListener('keydown', onKeydown));
onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown));
</script>

<template>
  <div
    v-if="store.isOpen && store.plan"
    id="plan"
    class="modal"
    role="dialog"
    aria-modal="true"
    aria-labelledby="planTitle"
    data-testid="plan-modal"
    @click.self="store.cancel()"
  >
    <div class="modal-card">
      <div class="card-h" style="margin: 0">
        <div>
          <h2 id="planTitle">{{ store.plan.title }}</h2>
          <p id="planSub" class="muted" style="margin: 2px 0 0; font-size: 12.5px">
            {{ store.plan.subtitle ?? '' }}
          </p>
        </div>
      </div>
      <ul v-if="alerts.length > 0" class="plan-alerts" role="note" data-testid="plan-alerts">
        <li v-for="(line, i) in alerts" :key="i">{{ line }}</li>
      </ul>
      <div v-if="links.length > 0" class="row plan-links">
        <a
          v-for="link in links"
          :key="link.url"
          class="btn sm"
          :href="link.url"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="plan-link"
          >{{ link.label }}</a
        >
      </div>
      <div v-if="slot" class="row plan-slot" data-testid="plan-slot">
        <label class="field"
          >{{ slot.label ?? 'Date' }}
          <input
            type="date"
            aria-label="Date du rendez-vous"
            :value="slot.date"
            :disabled="store.phase !== 'review' || store.rebuilding"
            @change="onSlotChange('date', ($event.target as HTMLInputElement).value)"
        /></label>
        <label class="field"
          >Heure
          <!-- Lot AI [27] : liste de créneaux (quart d'heure) au lieu du champ « heure » du navigateur. -->
          <TimeSelect
            aria-label="Heure du rendez-vous"
            :model-value="slot.time"
            :step="15"
            :disabled="store.phase !== 'review' || store.rebuilding"
            @update:model-value="(value) => onSlotChange('time', value)"
        /></label>
        <small v-if="slot.windowStart && slot.windowEnd" class="muted"
          >Période : {{ formatWeekdayShort(slot.windowStart) }} → {{ formatWeekdayShort(slot.windowEnd)
          }}{{ store.rebuilding ? ' · recalcul…' : '' }}</small
        >
      </div>
      <div v-if="projectType" class="field plan-type" data-testid="plan-project-type">
        <span>Type de projet</span>
        <div class="seg" role="group" aria-label="Type de projet">
          <button
            type="button"
            :aria-pressed="projectType.value === 'evolusite'"
            :disabled="store.phase !== 'review' || store.rebuilding"
            @click="onProjectType('evolusite')"
          >
            Evolusite (R3 → maquette → intégration)
          </button>
          <button
            type="button"
            :aria-pressed="projectType.value === 'partner'"
            :disabled="store.phase !== 'review' || store.rebuilding"
            @click="onProjectType('partner')"
          >
            Partenaire · intégration seule
          </button>
        </div>
        <small class="muted"
          >{{
            projectType.detected === 'partner'
              ? `Proposé d'office : client du canal « Partenaire webdesign »${projectType.partnerName ? `, apporteur ${projectType.partnerName}` : ''}.`
              : 'Planning complet habituel : R3, maquette, R4, retours, intégration, R5.'
          }}{{ store.rebuilding ? ' · recalcul…' : '' }}</small
        >
      </div>
      <div v-if="timeline.length > 0" class="plan-timeline" data-testid="plan-timeline">
        <div v-for="line in timeline" :key="line.label" class="tl">
          <span class="k">{{ line.label }}</span
          ><span
            ><b>{{ line.value }}</b> <small v-if="line.note" class="muted">{{ line.note }}</small></span
          >
        </div>
      </div>
      <div
        v-if="subcontracting && store.phase === 'review'"
        class="plan-subcontracting"
        data-testid="plan-subcontracting"
      >
        <SubcontractingBlock :quote-id="subcontracting.quoteId" @saved="onSubcontractingSaved" />
        <small v-if="subcontracting.subcontracted" class="muted" data-testid="plan-mockup-subcontracted"
          >Maquette sous-traitée : oui (planning sans la maquettiste).</small
        >
      </div>
      <ol v-if="guide.length > 0" class="plan-guide" data-testid="plan-guide">
        <li v-for="(line, i) in guide" :key="i">{{ line }}</li>
      </ol>
      <p v-else style="margin: 0; font-size: 13px">
        Voici ce qui va être fait. Décoche ce que tu ne veux pas, puis valide : rien n'est exécuté avant.
      </p>
      <ul class="plan-steps">
        <template v-for="(step, k) in store.plan.steps" :key="step.key">
          <li
            v-for="(later, j) in laterBefore(step.key)"
            :key="`later-${step.key}-${j}`"
            class="off later"
            data-testid="plan-later-step"
          >
            <input type="checkbox" disabled :aria-label="later.label" />
            <div class="plan-body">
              <b>{{ later.label }} <span class="later-tag">plus tard</span></b
              ><small v-if="later.detail">{{ later.detail }}</small>
            </div>
            <span class="chip c-mut">{{ later.via }}</span>
          </li>
          <li
            :data-step-key="step.key"
            :class="{
              off: !draftOf(step)?.checked,
              ok: store.shown[step.key]?.status === 'ok',
              failed: store.shown[step.key]?.status === 'error',
              sub: SUB_STEP_OPS.has(step.op) || subSteps.has(step.key),
            }"
          >
            <input
              :id="`plan-${k}`"
              type="checkbox"
              :checked="draftOf(step)?.checked"
              :disabled="store.phase !== 'review'"
              @change="store.toggle(step.key, ($event.target as HTMLInputElement).checked)"
            />
            <div class="plan-body">
              <label :for="`plan-${k}`"
                ><b>{{ step.label }}</b
                ><small v-if="step.detail">{{ step.detail }}</small></label
              >
              <select
                v-if="step.choices && step.choices.length > 0"
                class="planChoice"
                aria-label="Comment"
                :value="draftOf(step)?.choice"
                :disabled="store.phase !== 'review' || !draftOf(step)?.checked"
                @change="store.setChoice(step.key, ($event.target as HTMLSelectElement).value)"
              >
                <option v-for="choice in step.choices" :key="choice.value" :value="choice.value">
                  {{ choice.label }}
                </option>
              </select>
              <label v-for="field in step.editable ?? []" :key="field.field" class="field plan-edit">
                {{ field.label }}
                <textarea
                  v-if="field.kind === 'textarea'"
                  :value="draftOf(step)?.edits[field.field] ?? ''"
                  :disabled="store.phase !== 'review' || !draftOf(step)?.checked"
                  rows="3"
                  @input="store.setEdit(step.key, field.field, ($event.target as HTMLTextAreaElement).value)"
                ></textarea>
                <input
                  v-else
                  type="text"
                  :value="draftOf(step)?.edits[field.field] ?? ''"
                  :disabled="store.phase !== 'review' || !draftOf(step)?.checked"
                  @input="store.setEdit(step.key, field.field, ($event.target as HTMLInputElement).value)"
                />
              </label>
              <small v-if="store.shown[step.key]?.status === 'error'" class="step-error">
                {{ store.shown[step.key]?.message }}
              </small>
            </div>
            <span class="chip" :class="chip(step).cls" data-testid="step-status">{{ chip(step).text }}</span>
          </li>
        </template>
        <li
          v-for="(later, j) in laterAtEnd"
          :key="`later-end-${j}`"
          class="off later"
          data-testid="plan-later-step"
        >
          <input type="checkbox" disabled :aria-label="later.label" />
          <div class="plan-body">
            <b>{{ later.label }} <span class="later-tag">plus tard</span></b
            ><small v-if="later.detail">{{ later.detail }}</small>
          </div>
          <span class="chip c-mut">{{ later.via }}</span>
        </li>
      </ul>
      <p v-if="store.error" class="form-error" role="alert">{{ store.error }}</p>
      <div
        v-if="store.phase === 'results' && resultLinks.length > 0"
        class="row plan-links"
        data-testid="plan-result-links"
      >
        <a
          v-for="link in resultLinks"
          :key="link.url"
          class="btn sm primary"
          :href="link.url"
          target="_blank"
          rel="noopener noreferrer"
          >{{ link.label }}</a
        >
      </div>
      <ConsequencesBlock
        v-if="store.phase === 'results'"
        :consequences="store.consequences"
        :me-id="auth.me?.id ?? null"
        @navigate="store.cancel()"
      />
      <div class="modal-foot">
        <template v-if="store.phase === 'results'">
          <button class="btn primary" type="button" @click="store.cancel()">Fermer</button>
        </template>
        <template v-else>
          <button class="btn" type="button" :disabled="store.phase === 'executing'" @click="store.cancel()">
            Annuler
          </button>
          <button
            ref="goButton"
            class="btn primary"
            type="button"
            :disabled="store.phase === 'executing'"
            @click="store.execute()"
          >
            {{ submitLabel }}
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
.plan-steps li.sub {
  margin-left: 26px;
}
/* Lot BE : étape grisée « plus tard » (non exécutable). */
.later-tag {
  display: inline-block;
  font: 600 10px var(--f-mono);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 1px 5px;
  border-radius: 4px;
  background: color-mix(in srgb, var(--warn) 14%, transparent);
  color: var(--warn);
  vertical-align: 1px;
  margin-left: 4px;
}
.plan-type .seg button {
  white-space: normal;
}
.plan-subcontracting {
  display: grid;
  gap: 6px;
  padding: 12px;
  border: 1px solid var(--line);
  border-radius: var(--radius);
}
.plan-timeline {
  display: grid;
  gap: 4px;
  font-size: 13px;
}
.plan-timeline .tl {
  display: grid;
  grid-template-columns: 90px minmax(0, 1fr);
  gap: 8px;
}
.plan-timeline .k {
  color: var(--muted);
  font-size: 12px;
}
</style>
